<template>
  <div class="legal-page">
    <section class="legal-hero">
      <p class="eyebrow">ubuHle legal</p>
      <h1>Privacy Policy</h1>
      <p>
        Effective Date: July 26, 2025
      </p>
      <div class="legal-actions">
        <router-link to="/" class="soft-link">Back to home</router-link>
        <router-link to="/legal/contact-us" class="soft-link soft-link--accent">Contact us</router-link>
      </div>
    </section>

    <section class="legal-grid">
      <article>
        <h2>1. Information We Collect</h2>
        <h3>Personal Information</h3>
        <ul>
          <li>Name, email address, and phone number for account creation</li>
          <li>Profile information and preferences</li>
          <li>Booking and appointment history</li>
          <li>Payment information, processed securely through third-party providers</li>
        </ul>

        <h3>Location Information</h3>
        <ul>
          <li>Device location to find nearby salons and beauty services</li>
          <li>Location is only collected when the app is in use and with your explicit permission</li>
        </ul>

        <h3>Usage Information</h3>
        <ul>
          <li>App usage patterns and preferences</li>
          <li>Device information such as device type, operating system, and app version</li>
          <li>Log data for troubleshooting and app improvement</li>
        </ul>
      </article>

      <article>
        <h2>2. How We Use Your Information</h2>
        <ul>
          <li>Provide and improve our booking services</li>
          <li>Process appointments and payments</li>
          <li>Send booking confirmations and reminders</li>
          <li>Provide customer support</li>
          <li>Personalize your app experience</li>
          <li>Ensure platform security and prevent fraud</li>
        </ul>

        <h2>3. Information Sharing</h2>
        <p>We do not sell, trade, or rent your personal information to third parties. We may share information with:</p>
        <ul>
          <li>Service Providers: Beauty salons and barbers you book with</li>
          <li>Analytics Services: Anonymized usage data for app improvement</li>
          <li>Legal Requirements: When required by law or to protect our rights</li>
        </ul>

        <h2>4. Data Security</h2>
        <ul>
          <li>Data encryption in transit and at rest</li>
          <li>Secure authentication and authorization systems</li>
          <li>Regular security audits and updates</li>
          <li>Limited access to personal information on a need-to-know basis</li>
        </ul>
      </article>

      <article>
        <h2>5. Your Rights and Choices</h2>
        <ul>
          <li>Access, update, or delete your personal information</li>
          <li>Opt out of marketing communications</li>
          <li>Disable location services, which may limit app functionality</li>
          <li>Request data portability</li>
          <li>Delete your account and associated data</li>
        </ul>

        <h2>6. Children's Privacy</h2>
        <p>
          Our app is not intended for children under 13. We do not knowingly collect personal information from children
          under 13. If we become aware of such collection, we will delete the information immediately.
        </p>

        <h2>7. Location Services</h2>
        <p>
          Our app uses location services to find nearby beauty salons and services, provide accurate directions to appointments,
          and enhance location-based recommendations. Location access can be disabled in your device settings, though this may
          limit app functionality.
        </p>
      </article>

      <article>
        <h2>8. Cookies and Tracking</h2>
        <p>
          We use analytics tools to understand app usage and improve user experience. These tools may use cookies or similar
          technologies. You can opt out through your device settings.
        </p>

        <h2>9. Third-Party Services</h2>
        <ul>
          <li>Google Maps: For location and mapping services</li>
          <li>Payment Processors: For secure payment handling</li>
          <li>Analytics Providers: For app performance monitoring</li>
        </ul>

        <h2>10. Data Retention</h2>
        <p>
          We retain your information as long as your account is active, it is needed to provide our services, or it is required
          by law or for legitimate business purposes. You can request deletion of your data at any time.
        </p>
      </article>

      <article>
        <h2>11. International Data Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries other than your own. We ensure adequate protection
          through appropriate safeguards.
        </p>

        <h2>12. Changes to This Policy</h2>
        <p>
          We may update this privacy policy periodically. We will notify you of significant changes through the app or via email.
          Continued use of the app after changes constitutes acceptance of the updated policy.
        </p>

        <h2>13. Contact Us</h2>
        <p>If you have questions about this privacy policy or your data:</p>
        <div class="contact-card">
          <a href="mailto:solomzi.jikani@gmail.com">solomzi.jikani@gmail.com</a>
          <a href="tel:+27824503880">+27 82 450 3880</a>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy'
}
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(255, 157, 0, 0.12), transparent 30%),
    linear-gradient(180deg, #111113 0%, #09090a 100%);
  color: #f5f2eb;
  padding: 32px clamp(20px, 4vw, 56px) 72px;
}

.legal-hero,
.legal-grid {
  max-width: 1060px;
  margin: 0 auto;
}

.legal-hero {
  padding: 28px 0 40px;
}

.eyebrow {
  margin: 0 0 14px;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #ff9d00;
  font-size: 0.74rem;
  font-weight: 800;
}

h1 {
  margin: 0;
  font-size: clamp(2.8rem, 6vw, 5rem);
  line-height: 0.95;
  letter-spacing: -0.04em;
}

.legal-hero p {
  color: rgba(245, 242, 235, 0.72);
  font-size: 1rem;
  line-height: 1.7;
  margin: 16px 0 0;
}

.legal-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.soft-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  color: #f5f2eb;
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #1d1d22;
  font-weight: 700;
}

.soft-link--accent {
  background: rgba(255, 157, 0, 0.14);
  border-color: rgba(255, 157, 0, 0.22);
}

.legal-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.legal-grid article {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(20, 20, 24, 0.96);
  border-radius: 24px;
  padding: 22px;
  backdrop-filter: blur(18px);
}

.legal-grid h2 {
  margin: 0 0 14px;
  font-size: 1.2rem;
}

.legal-grid h3 {
  margin: 18px 0 10px;
  font-size: 0.98rem;
  color: #ffbe4d;
}

.legal-grid p,
.legal-grid li {
  color: rgba(245, 242, 235, 0.76);
  line-height: 1.7;
}

ul {
  margin: 0;
  padding-left: 18px;
}

li + li {
  margin-top: 8px;
}

.contact-card {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.contact-card a {
  color: #f5f2eb;
  text-decoration: none;
  border-radius: 16px;
  padding: 14px 16px;
  background: #1f1f25;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

@media (max-width: 900px) {
  .legal-grid {
    grid-template-columns: 1fr;
  }
}
</style>